import { sha256 } from 'lib/utils/createHashUtils'
import type { AssistantSurfaceSnapshotChunk, AssistantSurfaceSnapshotChunkRef } from 'tripPlanner/assistant/api/surfaceSnapshotContract'

// A second stable-stringify alongside lib/object/stableStringifyForCacheKey —
// deliberate, not a duplicate to consolidate. This one must byte-for-byte
// match svc-trip's own canonical-JSON recomputation of the same chunk payload
// (assistantSurfaceSnapshotChunkContentHash values are pinned against exact
// hashes in fingerprint.test.ts), so it mirrors native JSON.stringify
// semantics exactly — dropping undefined-valued object keys, nulling
// undefined array items and non-finite numbers — whereas
// stableStringifyForCacheKey serializes undefined as the literal text
// "undefined", which is fine for its own local cache-key purpose but would
// break hash parity with svc-trip here.
type StableJsonValue =
  | string
  | number
  | boolean
  | null
  | Array<StableJsonValue>
  | { [key: string]: StableJsonValue }

const isUnsupportedJsonValue = (value: unknown): boolean =>
  value === undefined ||
  typeof value === 'function' ||
  typeof value === 'symbol'

const compareCodeUnits = (left: string, right: string): number => {
  if (left < right) {
    return -1
  }
  if (left > right) {
    return 1
  }
  return 0
}

const normalizeStableJsonValue = (
  value: unknown,
  arrayItem = false,
): StableJsonValue | undefined => {
  if (value instanceof Date) {
    return value.toISOString()
  }

  if (isUnsupportedJsonValue(value)) {
    return arrayItem ? null : undefined
  }

  if (typeof value === 'bigint') {
    throw new TypeError('Cannot stable-stringify bigint values')
  }

  if (typeof value === 'number' && !Number.isFinite(value)) {
    return null
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizeStableJsonValue(item, true) ?? null)
  }

  if (value !== null && typeof value === 'object') {
    const normalized: { [key: string]: StableJsonValue } = {}

    Object.entries(value)
      .sort(([left], [right]) => compareCodeUnits(left, right))
      .forEach(([key, item]) => {
        const normalizedValue = normalizeStableJsonValue(item)

        if (normalizedValue !== undefined) {
          normalized[key] = normalizedValue
        }
      })

    return normalized
  }

  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value
  }

  throw new TypeError(`Cannot stable-stringify ${typeof value} values`)
}

export const stableJsonStringify = (value: unknown): string => {
  const normalized = normalizeStableJsonValue(value)
  return JSON.stringify(normalized) ?? 'null'
}

export const stableJsonSha256 = (value: unknown): string => sha256(stableJsonStringify(value))

export type AssistantSurfaceSnapshotChunkPayloadForHash =
  | {
    kind: 'recent_committed_state'
    recentCommittedState: ReadonlyArray<unknown>
  }
  | {
    kind: 'atoms'
    atoms: ReadonlyArray<unknown>
  }
  | {
    kind: 'targets'
    targets: ReadonlyArray<unknown>
  }
  | {
    kind: 'capabilities'
    capabilities: ReadonlyArray<unknown>
  }

export const assistantSurfaceSnapshotChunkContentHash = (
  chunk: AssistantSurfaceSnapshotChunkPayloadForHash,
): string => {
  switch (chunk.kind) {
    case 'recent_committed_state':
      return `sha256:${stableJsonSha256({
        kind: chunk.kind,
        recentCommittedState: chunk.recentCommittedState,
      })}`
    case 'atoms':
      return `sha256:${stableJsonSha256({
        kind: chunk.kind,
        atoms: chunk.atoms,
      })}`
    case 'targets':
      return `sha256:${stableJsonSha256({
        kind: chunk.kind,
        targets: chunk.targets,
      })}`
    case 'capabilities':
      return `sha256:${stableJsonSha256({
        kind: chunk.kind,
        capabilities: chunk.capabilities,
      })}`
  }
}

// Shared by every surface builder (offer page, trip planner, ...) -- this mapping is purely a
// function of the wire contract's chunk shape, with no surface-specific logic. Kept in one
// place so a future contract change (e.g. a new chunk kind) only needs updating here, not once
// per builder.
export const buildAssistantSurfaceSnapshotChunkRef = (
  chunk: AssistantSurfaceSnapshotChunk,
): AssistantSurfaceSnapshotChunkRef => {
  switch (chunk.kind) {
    case 'atoms':
      return { chunkId: chunk.chunkId, contentHash: chunk.contentHash, kind: chunk.kind, itemCount: chunk.atoms.length }
    case 'targets':
      return { chunkId: chunk.chunkId, contentHash: chunk.contentHash, kind: chunk.kind, itemCount: chunk.targets.length }
    case 'recent_committed_state':
      return { chunkId: chunk.chunkId, contentHash: chunk.contentHash, kind: chunk.kind, itemCount: chunk.recentCommittedState.length }
    case 'capabilities':
      return { chunkId: chunk.chunkId, contentHash: chunk.contentHash, kind: chunk.kind, itemCount: chunk.capabilities.length }
  }
}
